import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  useForm,
  Controller,
  useFieldArray,
  Control,
  FieldArrayWithId,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
  Collapse,
  Chip,
  Stack,
  Autocomplete,
  Button,
  TextField,
  Skeleton,
  CircularProgress,
  IconButton,
  Alert,
  Popover,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  NumericEntryField,
  NumericDisplayField,
} from "../../components/common/CustomFields";
import {
  Search as SearchIcon,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import {
  TypedSelectOption,
  UnitOption,
} from "../../types/common/selectOptions";
import {
  LowStockItem,
  LowStockResponse,
  LowStockLot,
} from "../../types/mrpTypes";
import { useTSQLowStock } from "../../hooks/useTSMRP";
import stringUtils from "../../utils/stringUtils";
import statusUtils from "../../utils/statusUtils";
import { paginationDefaults } from "../../config/settingsDefaults";
import "../../styles/chipOverrides";
import { DynamicRequestFilter } from "../../types/dynamicQueryTypes";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import { UOMUnitFull } from "../../types/uomTypes";

type UnitOpt = UnitOption<{ conversion: number }>;

// interface LotData {
//   lot_id: string;
//   pos_id: string;
//   lot_number: string;
//   expiration_date: Date | null;
//   status: string;
//   bin: string;
//   on_hand: number;
//   available: number;
// }

// interface ItemData {
//   id: string;
//   number: string;
//   name: string;
//   min_stock: number;
//   unit_type_id: string;
//   storage_unit_id: string;
//   storage_unit_symbol: string;
//   storage_unit_conversion: number;
//   lots: LotData[];
// }

const LotPopup: React.FC<{
  lots: LowStockLot[];
  isOpen: boolean;
  onClose: () => void;
  anchor: any;
  unitSymbol: string;
  unitConversion: number;
}> = ({ lots, isOpen, onClose, anchor, unitSymbol, unitConversion }) => {
  return (
    <Popover open={isOpen} anchorEl={anchor} onClose={onClose}>
      <List>
        <ListItem>
          <ListItemText>Lot</ListItemText>
          <ListItemText>Available</ListItemText>
          <ListItemText>On Hand</ListItemText>
          <ListItemText>Expiration</ListItemText>
        </ListItem>
        {lots.map((v) => (
          <ListItem>
            <ListItemText>{v.lot_number}</ListItemText>
            <ListItemText>
              {v.available
                ? (parseFloat(v.quantity) / unitConversion).toFixed(4)
                : 0}
              x {unitSymbol}
            </ListItemText>
            <ListItemText>
              {(parseFloat(v.quantity) / unitConversion).toFixed(4)}x{" "}
              {unitSymbol}
            </ListItemText>
            <ListItemText>
              {v.expiration_date
                ? new Date(v.expiration_date).toLocaleDateString("en-US")
                : "N/A"}
            </ListItemText>
          </ListItem>
        ))}
      </List>
    </Popover>
  );
};

const InventoryLine: React.FC<{
  itemData: LowStockItem;
  unitOpts: UnitOpt[];
}> = ({ itemData, unitOpts }) => {
  const [selectedUnitOpt, setSelectedUnitOpt] = useState<UnitOpt | null>(
    unitOpts.find((v) => v.value.id === itemData.storage_unit_id) ||
      unitOpts[0],
  );
  const [lotDetailsOpen, setLotDetailsOpen] = useState<boolean>(false);
  const anchorElRef = useRef<any>(null);
  const [onHand, avail] = useMemo(() => {
    let onHand = 0;
    let avail = 0;
    itemData.lots.forEach((val) => {
      const quant = parseFloat(val.quantity);
      onHand += quant;
      if (val.available) {
        avail += quant;
      }
    });
    return [onHand, avail];
  }, [itemData]);

  const handleLotPopupClose = () => {
    setLotDetailsOpen(false);
  };
  return (
    <TableRow>
      <TableCell>
        <Stack direction="column" gap={0.5}>
          <Typography variant="body1">{itemData.number}</Typography>
          <Typography variant="caption">{itemData.name}</Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Box display="flex" gap={0.5} alignItems={"center"}>
          {!selectedUnitOpt ? (
            <Typography variant="body1">
              Select an UOM to see Min Stock Count for that Unit
            </Typography>
          ) : (
            <Typography variant="body1">
              {(
                parseFloat(itemData.min_stock) /
                selectedUnitOpt.value.conversion
              ).toFixed(4)}
              x
            </Typography>
          )}
          <Autocomplete
            value={selectedUnitOpt}
            onChange={(e, v) => setSelectedUnitOpt(v)}
            options={unitOpts}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.value.id === val.value.id}
            fullWidth
            renderInput={(params) => <TextField {...params} label="UOM" />}
          />
        </Box>
      </TableCell>
      <TableCell>
        {!selectedUnitOpt ? (
          <Typography variant="body1">
            Select an UOM to see current inventory levels
          </Typography>
        ) : (
          <Stack direction="column" gap={0.5}>
            <Typography variant="body1">
              Available: {(avail / selectedUnitOpt.value.conversion).toFixed(4)}
              x {selectedUnitOpt.label}
            </Typography>
            <Typography variant="body1">
              On Hand: {(onHand / selectedUnitOpt.value.conversion).toFixed(4)}x{" "}
              {selectedUnitOpt.label}
            </Typography>
          </Stack>
        )}
      </TableCell>
      <TableCell>
        {!selectedUnitOpt ? (
          <Typography variant="body1">
            Select an UOM to see lot details
          </Typography>
        ) : (
          <>
            <Button
              variant="contained"
              color="info"
              ref={anchorElRef}
              onClick={() => setLotDetailsOpen((prev) => !prev)}
            >
              View Lots
            </Button>
            <LotPopup
              lots={itemData.lots}
              isOpen={lotDetailsOpen}
              onClose={handleLotPopupClose}
              anchor={anchorElRef.current}
              unitSymbol={selectedUnitOpt.label}
              unitConversion={selectedUnitOpt.value.conversion}
            />
          </>
        )}
      </TableCell>
    </TableRow>
  );
};

const PageLowInventory: React.FC = () => {
  const {
    data: lowStockResponse,
    isLoading: lowStockLoading,
    isFetching: lowStockFetching,
  } = useTSQLowStock(true);
  const { user } = useAuth();
  const [items, units]: [
    items: LowStockItem[],
    units: { [key: string]: UnitOpt[] },
  ] = useMemo(() => {
    if (lowStockResponse?.items) {
      const units: { [key: string]: UnitOpt[] } = {};
      lowStockResponse.units.forEach((val) => {
        if (units[val.uom_type_id] === undefined) {
          units[val.uom_type_id] = [];
        }
        units[val.uom_type_id].push({
          label: val.unit_symbol,
          type: "unit",
          value: {
            id: val.id,
            conversion: parseFloat(val.conversion_factor),
          },
        });
      });
      return [lowStockResponse.items, units];
    } else {
      return [[], {}];
    }
  }, [lowStockResponse]);
  return (
    <Stack direction="column" gap={1}>
      <Box display="flex" gap={1}>
        <Typography variant="h4">Low Stock Items</Typography>
        {user?.is_superuser && (
          <Button
            variant="outlined"
            onClick={() => {
              console.log("Items", items);
              console.log("Units", units);
            }}
          >
            DEBUG PRINT
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Min Stock/UOM</TableCell>
              <TableCell>Current Inventory</TableCell>
              <TableCell>Lot Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lowStockFetching ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Box display="flex" gap={0.5}>
                    <Typography variant="body1">Loading Data...</Typography>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              items.map((i, ind) => (
                <InventoryLine
                  key={ind}
                  itemData={i}
                  unitOpts={units[i.unit_type_id]}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default PageLowInventory;
